import { MODAL_TYPE } from 'config/Constants';
import { useStore } from 'hooks/useStore';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CompletedJob, JobPasteServices } from 'services/jobPasteServices';
export interface AddJobFormData {
  job_id: string;
  priority: string;
  status: string;
  room: string;
  ndc: string;
  description: string;
  qty_of_containers_to_fill: string;
  qty_per_container: string;
  run_type: string;
  qty_units_to_package: string;
  confirmed_date: Date | null;
  initial_note?: string;
  number_of_bottles_at_shift_start: string | null;
  job_start_time: Date | null | string;
}

export const useAddJobModal = () => {
  const { runTypeStore, roomStore, statusCodesStore } = useStore();

  const [formData, setFormData] = useState<AddJobFormData>({
    confirmed_date: null,
    description: '',
    job_id: '',
    job_start_time: null,
    ndc: '',
    number_of_bottles_at_shift_start: '',
    priority: '3',
    qty_of_containers_to_fill: '',
    qty_per_container: '',
    qty_units_to_package: '',
    room: '',
    status: '',
    run_type: '',
    initial_note: ''
  });

  // Completed jobs search state
  const [completedJobsSearchTerm, setCompletedJobsSearchTerm] = useState<string>('');
  const [completedJobs, setCompletedJobs] = useState<CompletedJob[]>([]);
  const [completedJobsLoading, setCompletedJobsLoading] = useState(false);
  const [completedJobsSearchLoading, setCompletedJobsSearchLoading] = useState(false);
  const [selectedCompletedJob, setSelectedCompletedJob] = useState<CompletedJob | null>(null);

  const roomOptionLoading = roomStore.isLoading;
  const jobTypesOptionLoading = runTypeStore.isLoading;
  const statusOptionsLoading = statusCodesStore.isLoading;
  const environment = import.meta.env.VITE_ENVIRONMENT;

  // Prepare room options from roomStore
  const roomOptions = roomStore.rooms.map(room => ({
    label: `${room.room_long}`,
    value: room.room_short
  }));

  const jobTypes = runTypeStore.runTypes.map(jobType => ({
    label: `${jobType.description}`,
    value: `${jobType.description.toLowerCase()}`
  }));

  const statusOptions = [
    {
      label: 'Ready',
      value: 'ready'
    }
  ];

  const priorityOptions = [
    { label: '1', value: '1' },
    { label: '3', value: '3' }
  ];

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { GlobalModalStore, JobPasteStore } = useStore();

  const { getCurrentInstance, toggleModal } = GlobalModalStore;
  const { saveSingleJob, saveSingleJobLoading } = JobPasteStore;

  const onClose = () => {
    toggleModal(MODAL_TYPE.PPS_JPB_PASTE_ADD_JOB_MODAL);
  };

  // Search completed jobs function
  const searchCompletedJobsInternal = async (searchTerm: string) => {
    setCompletedJobsSearchLoading(true);
    try {
      const response = await JobPasteServices.fetchCompletedJobs(searchTerm);
      setCompletedJobs(response.jobs);
    } catch (error) {
      setCompletedJobs([]);
    } finally {
      setCompletedJobsSearchLoading(false);
    }
  };

  // Debounced search function
  const debouncedSearchCompletedJobs = useCallback(
    debounce((searchTerm: string) => {
      searchCompletedJobsInternal(searchTerm);
    }, 300), // 300ms debounce delay
    []
  );

  // Search completed jobs - exposed function
  const searchCompletedJobs = (searchTerm: string) => {
    debouncedSearchCompletedJobs(searchTerm);
  };

  // Load default completed jobs when modal opens
  const loadDefaultCompletedJobs = useCallback(async () => {
    setCompletedJobsLoading(true);
    try {
      const response = await JobPasteServices.fetchCompletedJobs(''); // Empty string for default top 20
      setCompletedJobs(response.jobs);
    } catch (error) {
      setCompletedJobs([]);
    } finally {
      setCompletedJobsLoading(false);
    }
  }, []);

  // Load default jobs when modal becomes visible
  const visibility = getCurrentInstance(MODAL_TYPE.PPS_JPB_PASTE_ADD_JOB_MODAL);

  useEffect(() => {
    if (visibility) {
      loadDefaultCompletedJobs();
    } else {
      // Clear state when modal closes
      setCompletedJobs([]);
      setCompletedJobsSearchTerm('');
      setSelectedCompletedJob(null);
    }
  }, [visibility, loadDefaultCompletedJobs]);

  // Debug: Track formData changes
  useEffect(() => {
    // FormData updated
  }, [formData]);

  // Prefill form from selected completed job
  const prefillFormFromCompletedJob = (completedJob: CompletedJob) => {
    try {
      if (!completedJob) {
        return;
      }

      // Check if essential properties exist
      setSelectedCompletedJob(completedJob);

      // Format the date - the API returns dates in MM-DD-YYYY format
      let confirmedDate = null;
      let jobStartTime = null;

      try {
        if (completedJob.confirmedDate) {
          // Parse MM-DD-YYYY format correctly
          const dateParts = completedJob.confirmedDate.split('-');
          if (dateParts.length === 3) {
            // MM-DD-YYYY format: [MM, DD, YYYY]
            const month = parseInt(dateParts[0]) - 1; // JavaScript months are 0-indexed
            const day = parseInt(dateParts[1]);
            const year = parseInt(dateParts[2]);
            confirmedDate = new Date(year, month, day);
          } else {
            // Fallback to direct parsing
            confirmedDate = new Date(completedJob.confirmedDate);
          }
        }
        if (completedJob.jobStartTime) {
          jobStartTime = new Date(completedJob.jobStartTime);
        }
      } catch (dateError) {
        // Use the original strings if parsing fails
        confirmedDate = completedJob.confirmedDate ? new Date(completedJob.confirmedDate) : null;
        jobStartTime = completedJob.jobStartTime ? new Date(completedJob.jobStartTime) : null;
      }

      // Parsed dates for form prefill

      // Create the new form data with proper null/undefined checks
      const jobIdValue = completedJob.jobId || completedJob.id || '';

      console.log('completedJob', { completedJob });

      // Find the short room code by looking up the long room name in roomOptions
      const roomShortCode = completedJob.room
        ? roomOptions.find(option => option.label.toLowerCase() === completedJob.room.toLowerCase())
            ?.value || ''
        : '';

      console.log({ roomShortCode });
      const newFormData = {
        job_id: `${jobIdValue}_1`, // Append "_1" to avoid duplicates
        priority: completedJob.priority?.toString() || '3', // Default to '3' if undefined
        status: '', // Status should be empty
        room: roomShortCode, // Use the short room code from lookup
        ndc: completedJob.ndc || '',
        description: completedJob.description || '',
        qty_of_containers_to_fill: completedJob.qtyOfContainers?.toString() || '',
        qty_per_container: completedJob.qtyPerContainer?.toString() || '',
        run_type: completedJob.type?.toLowerCase() || '', // Convert to lowercase to match form options
        qty_units_to_package: completedJob.qtyUnitsToPackage || '',
        confirmed_date: confirmedDate,
        number_of_bottles_at_shift_start:
          completedJob.numberOfBottlesInAtShiftStart?.toString() || '',
        job_start_time: jobStartTime,
        initial_note:
          completedJob.notes && completedJob.notes.length > 0 ? completedJob.notes[0].noteText : ''
      };

      // Raw completed job data processed

      // Use functional update to ensure we get the current state
      setFormData(prevFormData => {
        const updatedFormData = {
          ...prevFormData,
          ...newFormData
        };
        return updatedFormData;
      });

      // Clear the search after prefilling
      // setCompletedJobsSearchTerm(''); // Don't clear here, let the select handler do it
    } catch (error) {
      if (environment === 'development') {
        console.error('Error in prefillFormFromCompletedJob:', error);
      }
      toast.error('Error in prefilling form from completed job');
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.job_id) {
      newErrors.job_id = 'Job ID is required';
    }
    if (!formData.description) {
      newErrors.description = 'Drug name is required';
    }
    if (!formData.ndc) {
      newErrors.ndc = 'Drug finished goods ndc is required';
    }
    if (!formData.qty_of_containers_to_fill) {
      newErrors.qty_of_containers_to_fill = 'Quantity of containers to fill is required';
    }
    if (!formData.qty_per_container) {
      newErrors.qty_per_container = 'Quantity per container is required';
    }
    if (!formData.qty_units_to_package) {
      newErrors.qty_units_to_package = 'Quantity of units to package is required';
    }
    if (!formData.run_type) {
      newErrors.run_type = 'Run Type is required';
    }
    if (!formData.room.length) {
      newErrors.room = 'Room is required';
    }
    if (!formData.confirmed_date) {
      newErrors.confirmed_date = 'Roconfirmed date is required';
    }

    // 1. NDC format validation (xxxxx-xxx-xx with only numbers)
    if (formData.ndc) {
      const ndcRegex = /^\d{5}-\d{3}-\d{2}$/;
      if (!ndcRegex.test(formData.ndc)) {
        newErrors.ndc = 'NDC must be in format xxxxx-xxx-xx (numbers only)';
      }
    }

    if (formData.qty_units_to_package) {
      if (formData.qty_units_to_package !== 'SUPERBULK') {
        const value = Number(formData.qty_units_to_package);
        if (isNaN(value)) {
          newErrors.qty_units_to_package =
            'Quantity of units to package must be a number or SUPERBULK';
        }
      }
    }

    // 2. Quantity fields must be non-negative if provided
    const quantityFields = [
      'qty_of_containers_to_fill',
      'qty_per_container',
      'number_of_bottles_at_shift_start'
    ];

    quantityFields.forEach(field => {
      if (
        formData[field as keyof AddJobFormData] !== '' &&
        formData[field as keyof AddJobFormData] !== null
      ) {
        const value = Number(formData[field as keyof AddJobFormData]);
        if (isNaN(value) || value < 0) {
          newErrors[field as keyof AddJobFormData] = `${field.replace(
            /_/g,
            ' '
          )} must be a non-negative number`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (formData: AddJobFormData) => {
    // Check if this is a reopened job (has selectedCompletedJob)
    const isReopen = selectedCompletedJob !== null;

    const response = await saveSingleJob(formData, isReopen);

    if (response) {
      setFormData({
        confirmed_date: null,
        description: '',
        job_id: '',
        job_start_time: null,
        ndc: '',
        number_of_bottles_at_shift_start: '',
        priority: '3',
        qty_of_containers_to_fill: '',
        qty_per_container: '',
        qty_units_to_package: '',
        room: '',
        status: '',
        run_type: '',
        initial_note: ''
      });

      // Clear completed jobs search state
      setCompletedJobsSearchTerm('');
      setCompletedJobs([]);
      setSelectedCompletedJob(null);

      onClose();
    }
  };

  return {
    onClose,
    visibility,
    formData,
    setFormData,
    errors,
    setErrors,
    jobTypes,
    priorityOptions,
    validateForm,
    handleSubmit,
    roomOptions,
    statusOptions,
    saveSingleJobLoading,
    roomOptionLoading,
    jobTypesOptionLoading,
    statusOptionsLoading,
    // Completed jobs search functionality
    completedJobsSearchTerm,
    setCompletedJobsSearchTerm,
    completedJobs,
    setCompletedJobs,
    completedJobsLoading,
    completedJobsSearchLoading,
    selectedCompletedJob,
    searchCompletedJobs,
    loadDefaultCompletedJobs,
    prefillFormFromCompletedJob
  };
};
